import { apiClient as api } from './api';

export interface Transaction {
  id: string;
  type: 'INCOME' | 'EXPENSE';
  category: string;
  description: string;
  amount: number;
  gstAmount?: number;
  paymentMethod: string;
  status: string;
  date: string;
  referenceId?: string;
  referenceType?: string;
  customerId?: string;
  customerName?: string;
  staffId?: string;
  staffName?: string;
  receiptNumber?: string;
  vendorName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionFilters {
  type?: 'INCOME' | 'EXPENSE';
  category?: string;
  paymentMethod?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  customerId?: string;
  staffId?: string;
  vendorName?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface TransactionSummary {
  totalIncome: number;
  totalExpenses: number;
  netAmount: number;
  totalTransactions: number;
  incomeTransactions: number;
  expenseTransactions: number;
  incomeByMethod: Array<{
    paymentMethod: string;
    amount: number;
    count: number;
  }>;
  expensesByCategory: Array<{
    category: string;
    amount: number;
    count: number;
  }>;
  monthlyTrends: Array<{
    month: string;
    income: number;
    expenses: number;
    net: number;
  }>;
}

export interface CashFlowAnalysis {
  openingBalance: number;
  totalInflow: number;
  totalOutflow: number;
  closingBalance: number;
  cashFlowByDay: Array<{
    date: string;
    inflow: number;
    outflow: number;
    netFlow: number;
    runningBalance: number;
  }>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class TransactionService {
  /**
   * Get transactions with filtering and pagination
   */
  async getTransactions(filters: TransactionFilters = {}): Promise<PaginatedResponse<Transaction>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/transactions?${params.toString()}`);

    // Handle the API response structure
    return {
      data: response.data || [],
      pagination: response.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
      },
    };
  }

  /**
   * Get transaction by ID
   */
  async getTransactionById(id: string): Promise<Transaction> {
    const response = await api.get(`/transactions/${id}`);
    return response.data;
  }

  /**
   * Get transaction summary and analytics
   */
  async getTransactionSummary(startDate?: string, endDate?: string): Promise<TransactionSummary> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get<any>(`/transactions/summary?${params.toString()}`);
    const data = response?.data ?? response;
    return data ?? {
      totalIncome: 0,
      totalExpenses: 0,
      netAmount: 0,
      totalTransactions: 0,
      incomeTransactions: 0,
      expenseTransactions: 0,
      incomeByMethod: [],
      expensesByCategory: [],
      monthlyTrends: [],
    };
  }

  /**
   * Get cash flow analysis
   */
  async getCashFlowAnalysis(startDate?: string, endDate?: string): Promise<CashFlowAnalysis> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/transactions/cash-flow?${params.toString()}`);
    return response.data;
  }
}

export const transactionService = new TransactionService();
